import { useCallback, useEffect, useMemo } from "react";

const normalizeTabKey = (key) => {
  if (typeof key !== "string") {
    return key;
  }

  if (key.startsWith("#.$")) {
    return "#" + key.slice(3);
  }

  if (key.startsWith(".$")) {
    return key.slice(2);
  }

  return key;
};

const getValidTabKey = (hash, availableTabs, initialValue) => {
  const normalizedHash = normalizeTabKey(hash);
  const normalizedInitialValue = normalizeTabKey(initialValue);

  if (availableTabs.includes(normalizedHash)) {
    return normalizedHash;
  }

  if (availableTabs.includes(normalizedInitialValue)) {
    return normalizedInitialValue;
  }

  return availableTabs[0] || normalizedInitialValue;
};

/**
 * Handle tab change.
 *
 * @hook
 * @alias useTabChange
 * @param {object} props
 * @param {string} initialValue
 * @param {array} availableTabs
 * @returns {array} activeTab, handleTabChange
 */
function useTabChange(props, initialValue, availableTabs = []) {
  const history = props?.history;
  const pathname = props?.location?.pathname;
  const search = props?.location?.search;
  const locationHash = props?.location?.hash;
  const locationState = props?.location?.state;
  const availableTabsKey = availableTabs.join("|");

  const normalizedTabs = useMemo(
    () => availableTabs.map((tab) => normalizeTabKey(tab)).filter(Boolean),
    [availableTabsKey]
  );

  const activeTab = getValidTabKey(locationHash, normalizedTabs, initialValue);

  useEffect(() => {
    if (!history || typeof pathname !== "string") {
      return;
    }

    const normalizedLocationHash = normalizeTabKey(locationHash);

    if (normalizedLocationHash === activeTab) {
      return;
    }

    history.replace({
      pathname,
      search,
      hash: activeTab,
      state: locationState,
    });
  }, [activeTab, history, locationHash, locationState, pathname, search]);

  const handleTabChange = useCallback(
    (nextKey) => {
      if (!history || typeof pathname !== "string") {
        return;
      }

      const nextTab = getValidTabKey(nextKey, normalizedTabs, initialValue);
      const normalizedLocationHash = normalizeTabKey(locationHash);

      if (nextTab === normalizedLocationHash) {
        return;
      }

      history.push({
        pathname,
        search,
        hash: nextTab,
        state: locationState,
      });
    },
    [history, initialValue, locationHash, locationState, normalizedTabs, pathname, search]
  );

  return [activeTab, handleTabChange];
}

export default useTabChange;
